/**
 * <ry-split>
 *
 * Two-column split layout with optional resizable divider and persistence.
 *
 * Usage:
 * <ry-split>
 *   <div>Main content</div>
 *   <div>Sidebar</div>
 * </ry-split>
 *
 * Resizable with persistence:
 * <ry-split resizable persist="my-panel">
 *   <div>Main</div>
 *   <div>Panel</div>
 * </ry-split>
 *
 * CSS custom properties:
 *   --ry-split-width:     Sidebar width (default 300px)
 *   --ry-split-min-width: Sidebar minimum during resize (default 100px)
 *   --ry-split-max-width: Sidebar maximum during resize (default 80% of container)
 */

import { RyElement } from '../core/ry-element.js';

export class RySplit extends RyElement {
  #handle: HTMLElement | null = null;
  #dragging = false;
  #startX = 0;
  #startWidth = 0;

  setup(): void {
    if (!this.hasAttribute('resizable')) return;

    // Restore persisted width
    this.#restore();

    // Create resize handle
    this.#handle = document.createElement('div');
    this.#handle.setAttribute('data-ry-target', 'handle');
    this.#handle.className = 'ry-split__handle';
    this.#handle.setAttribute('role', 'separator');
    this.#handle.setAttribute('aria-orientation', 'vertical');
    this.#handle.setAttribute('tabindex', '0');

    // Insert handle before the last child (the sidebar)
    const sidebar = this.lastElementChild;
    if (sidebar) {
      this.insertBefore(this.#handle, sidebar);
    }

    this.on(this.#handle, 'mousedown', this.#onMouseDown);
    this.on(this.#handle, 'touchstart', this.#onTouchStart as EventListener);
    this.on(this.#handle, 'keydown', this.#onKeyDown);
    this.on(this.#handle, 'dblclick', this.#onDoubleClick);
  }

  #getSidebar(): HTMLElement | null {
    return this.lastElementChild as HTMLElement | null;
  }

  #getCurrentWidth(): number {
    const sidebar = this.#getSidebar();
    if (!sidebar) return 300;
    return sidebar.getBoundingClientRect().width;
  }

  #getMinWidth(): number {
    const val = getComputedStyle(this).getPropertyValue('--ry-split-min-width').trim();
    return val ? parseFloat(val) : 100;
  }

  #getMaxWidth(): number {
    const val = getComputedStyle(this).getPropertyValue('--ry-split-max-width').trim();
    if (val) return parseFloat(val);
    return this.getBoundingClientRect().width * 0.8;
  }

  #setWidth(px: number): void {
    const min = this.#getMinWidth();
    const max = this.#getMaxWidth();
    const clamped = Math.round(Math.max(min, Math.min(max, px)));
    this.style.setProperty('--ry-split-width', `${clamped}px`);
  }

  #onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    this.#startDrag(e.clientX);
    const onMove = (ev: MouseEvent) => this.#onDrag(ev.clientX);
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      this.#endDrag();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  #onTouchStart = (e: TouchEvent): void => {
    const touch = e.touches[0];
    if (!touch) return;
    e.preventDefault();
    this.#startDrag(touch.clientX);
    const onMove = (ev: TouchEvent) => {
      const t = ev.touches[0];
      if (t) this.#onDrag(t.clientX);
    };
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      this.#endDrag();
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  };

  #startDrag(clientX: number): void {
    this.#dragging = true;
    this.#startX = clientX;
    this.#startWidth = this.#getCurrentWidth();
    this.setAttribute('data-ry-resizing', '');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  #onDrag(clientX: number): void {
    if (!this.#dragging) return;
    // Dragging left increases sidebar width (sidebar is on the right)
    const delta = this.#startX - clientX;
    this.#setWidth(this.#startWidth + delta);
  }

  #endDrag(): void {
    this.#dragging = false;
    this.removeAttribute('data-ry-resizing');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    this.#persist();
    this.emit('resize', { width: this.#getCurrentWidth() });
  }

  #onKeyDown = (e: KeyboardEvent): void => {
    const step = e.shiftKey ? 50 : 10;
    let width = this.#getCurrentWidth();

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.#setWidth(width + step); // Left grows sidebar
      this.#persist();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.#setWidth(width - step); // Right shrinks sidebar
      this.#persist();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this.#setWidth(this.#getMinWidth());
      this.#persist();
    } else if (e.key === 'End') {
      e.preventDefault();
      this.#setWidth(this.#getMaxWidth());
      this.#persist();
    }
  };

  #onDoubleClick = (): void => {
    // Reset to default
    this.style.removeProperty('--ry-split-width');
    this.#clearPersist();
    this.emit('resize', { width: this.#getCurrentWidth() });
  };

  // Persistence
  #persistKey(): string | null {
    const key = this.getAttribute('persist');
    return key ? `ry-split:${key}` : null;
  }

  #persist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try {
      localStorage.setItem(key, String(this.#getCurrentWidth()));
    } catch { /* storage full or unavailable */ }
  }

  #restore(): void {
    const key = this.#persistKey();
    if (!key) return;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const px = parseFloat(saved);
        if (!isNaN(px)) {
          this.style.setProperty('--ry-split-width', `${px}px`);
        }
      }
    } catch { /* storage unavailable */ }
  }

  #clearPersist(): void {
    const key = this.#persistKey();
    if (!key) return;
    try { localStorage.removeItem(key); } catch { /* */ }
  }
}

customElements.define('ry-split', RySplit);
